// src/services/suppliersService.js
import ApiService from './api'
import AppURLs from '../config/urls'
import GRNService from './grnService'

class SuppliersService {
  
  // 🏢 Get all suppliers with pagination support
  async getAllSuppliers(params = {}) {
    try {
      console.log('🏢 Fetching all suppliers with params:', params)
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.GET_ALL, params)
      console.log('✅ Suppliers fetched successfully:', response)
      
      // Calculate actual purchase totals from GRN data
      if (response?.data?.suppliers) {
        const suppliersWithTotals = await this.calculateSupplierPurchaseTotals(response.data.suppliers)
        return {
          ...response,
          data: {
            ...response.data,
            suppliers: suppliersWithTotals
          }
        }
      }
      
      return response
    } catch (error) {
      console.error('❌ Error fetching suppliers:', error)
      throw error
    }
  }

  // 🏢 Get supplier by ID
  async getSupplierById(id) {
    try {
      console.log('🏢 Fetching supplier by ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.GET_BY_ID, { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Supplier fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching supplier:', error)
      throw error
    }
  }

  // 🏢 Create new supplier
  async createSupplier(supplierData) {
    try {
      console.log('🏢 Creating new supplier with data:', supplierData)
      
      // Validate required fields
      this.validateSupplierData(supplierData)
      
      const response = await ApiService.post(AppURLs.SUPPLIERS.CREATE, supplierData)
      console.log('✅ Supplier created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating supplier:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate data)
        const conflictError = new Error(error.response.data?.message || 'Supplier already exists')
        conflictError.response = error.response
        throw conflictError
      }
      
      throw error
    }
  }

  // 🏢 Update supplier using PATCH
  async updateSupplier(id, supplierData) {
    try {
      console.log('🏢 Updating supplier:', { id, supplierData })
      
      // Validate partial data for updates
      this.validatePartialSupplierData(supplierData)
      
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.UPDATE, { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, supplierData)
      console.log('✅ Supplier updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating supplier:', error)
      
      // Enhanced error handling for backend validation
      if (error.response?.status === 400 || error.response?.status === 422) {
        // Backend validation error
        const backendError = new Error(error.response.data?.message || 'Validation failed')
        backendError.response = error.response
        throw backendError
      } else if (error.response?.status === 409) {
        // Conflict error (duplicate data)
        const conflictError = new Error(error.response.data?.message || 'Supplier data conflicts with existing record')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Supplier not found')
        notFoundError.response = error.response
        throw notFoundError
      }
      
      throw error
    }
  }

  // 🏢 Delete supplier
  async deleteSupplier(id) {
    try {
      console.log('🏢 Deleting supplier with ID:', id)
      const endpoint = ApiService.replaceUrlParams(AppURLs.SUPPLIERS.DELETE, { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Supplier deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting supplier:', error)
      
      // Enhanced error handling for delete operations
      if (error.response?.status === 404) {
        // Not found error
        const notFoundError = new Error('Supplier not found')
        notFoundError.response = error.response
        throw notFoundError
      } else if (error.response?.status === 409) {
        // Conflict error (cannot delete due to dependencies)
        const conflictError = new Error(error.response.data?.message || 'Cannot delete supplier - it may have associated records')
        conflictError.response = error.response
        throw conflictError
      } else if (error.response?.status === 403) {
        // Forbidden error
        const forbiddenError = new Error('You do not have permission to delete this supplier')
        forbiddenError.response = error.response
        throw forbiddenError
      }
      
      throw error
    }
  }

  // 🏢 Search suppliers
  async searchSuppliers(query, params = {}) {
    try {
      console.log('🏢 Searching suppliers with query:', query)
      const searchParams = { ...params, q: query }
      const response = await ApiService.get(AppURLs.SUPPLIERS.SEARCH, searchParams)
      console.log('✅ Supplier search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching suppliers:', error)
      throw error
    }
  }

  // 🏢 Validate supplier name
  async validateName(name, excludeId = null) {
    try {
      console.log('🏢 Validating supplier name:', name)
      const params = { name }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_NAME, params)
      console.log('✅ Name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier name:', error)
      throw error
    }
  }

  // 🏢 Validate mobile number
  async validateMobile(mobile, excludeId = null) {
    try {
      console.log('🏢 Validating supplier mobile:', mobile)
      const params = { mobile }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_MOBILE, params)
      console.log('✅ Mobile validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier mobile:', error)
      throw error
    }
  }

  // 🏢 Validate email
  async validateEmail(email, excludeId = null) {
    try {
      console.log('🏢 Validating supplier email:', email)
      const params = { email }
      if (excludeId) params.excludeId = excludeId
      
      const response = await ApiService.get(AppURLs.SUPPLIERS.VALIDATE_EMAIL, params)
      console.log('✅ Email validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating supplier email:', error)
      throw error
    }
  }

  // 🏢 Bulk delete suppliers
  async bulkDelete(ids) {
    try {
      console.log('🏢 Bulk deleting suppliers with IDs:', ids)
      const response = await ApiService.post(AppURLs.SUPPLIERS.BULK_DELETE, { ids })
      console.log('✅ Bulk delete completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk delete:', error)
      throw error
    }
  }

  // 🏢 Bulk status update
  async bulkUpdateStatus(ids, isActive) {
    try {
      console.log('🏢 Bulk updating supplier status:', { ids, isActive })
      const response = await ApiService.post(AppURLs.SUPPLIERS.BULK_STATUS, { ids, isActive })
      console.log('✅ Bulk status update completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error in bulk status update:', error)
      throw error
    }
  }

  // 🏢 Get suppliers statistics
  async getStats() {
    try {
      console.log('🏢 Fetching supplier statistics')
      const response = await ApiService.get(AppURLs.SUPPLIERS.STATS)
      console.log('✅ Supplier stats fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching supplier stats:', error)
      throw error
    }
  }

  // 🔒 Validation Methods

  // Validate supplier data for creation
  validateSupplierData(data) {
    const errors = []

    // Required fields validation
    if (!data.name || typeof data.name !== 'string' || data.name.trim().length === 0) {
      errors.push('Supplier name is required and must be a non-empty string')
    }

    if (!data.mobile || typeof data.mobile !== 'string' || data.mobile.trim().length === 0) {
      errors.push('Mobile number is required and must be a non-empty string')
    }

    if (!data.contactPerson || typeof data.contactPerson !== 'string' || data.contactPerson.trim().length === 0) {
      errors.push('Contact person is required and must be a non-empty string')
    }

    // Email validation (optional but must be valid if provided)
    if (data.email && data.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.push('Email must be a valid email address')
      }
    }

    // Mobile number format validation
    if (data.mobile) {
      const mobileRegex = /^[0-9+\-\s()]+$/
      if (!mobileRegex.test(data.mobile)) {
        errors.push('Mobile number must contain only numbers, +, -, spaces, and parentheses')
      }
    }

    // Account information validation (optional but must be complete if provided)
    if (data.accountInformation) {
      const { accountName, accountNumber, bankName, branchName } = data.accountInformation
      
      if (accountName || accountNumber || bankName || branchName) {
        if (!accountName || accountName.trim().length === 0) {
          errors.push('Account name is required when account information is provided')
        }
        if (!accountNumber || accountNumber.trim().length === 0) {
          errors.push('Account number is required when account information is provided')
        }
        if (!bankName || bankName.trim().length === 0) {
          errors.push('Bank name is required when account information is provided')
        }
        if (!branchName || branchName.trim().length === 0) {
          errors.push('Branch name is required when account information is provided')
        }
      }
    }

    // String length validations
    if (data.name && data.name.length > 100) {
      errors.push('Supplier name must not exceed 100 characters')
    }

    if (data.mobile && data.mobile.length > 20) {
      errors.push('Mobile number must not exceed 20 characters')
    }

    if (data.contactPerson && data.contactPerson.length > 100) {
      errors.push('Contact person name must not exceed 100 characters')
    }

    if (data.email && data.email.length > 100) {
      errors.push('Email must not exceed 100 characters')
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // Validate partial supplier data for updates
  validatePartialSupplierData(data) {
    const errors = []

    // Optional fields validation (only validate if provided)
    if (data.name !== undefined) {
      if (typeof data.name !== 'string' || data.name.trim().length === 0) {
        errors.push('Supplier name must be a non-empty string')
      }
      if (data.name.length > 100) {
        errors.push('Supplier name must not exceed 100 characters')
      }
    }

    if (data.mobile !== undefined) {
      if (typeof data.mobile !== 'string' || data.mobile.trim().length === 0) {
        errors.push('Mobile number must be a non-empty string')
      }
      if (data.mobile.length > 20) {
        errors.push('Mobile number must not exceed 20 characters')
      }
      const mobileRegex = /^[0-9+\-\s()]+$/
      if (!mobileRegex.test(data.mobile)) {
        errors.push('Mobile number must contain only numbers, +, -, spaces, and parentheses')
      }
    }

    if (data.contactPerson !== undefined) {
      if (typeof data.contactPerson !== 'string' || data.contactPerson.trim().length === 0) {
        errors.push('Contact person must be a non-empty string')
      }
      if (data.contactPerson.length > 100) {
        errors.push('Contact person name must not exceed 100 characters')
      }
    }

    if (data.email !== undefined && data.email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(data.email)) {
        errors.push('Email must be a valid email address')
      }
      if (data.email.length > 100) {
        errors.push('Email must not exceed 100 characters')
      }
    }

    if (data.isActive !== undefined && typeof data.isActive !== 'boolean') {
      errors.push('isActive must be a boolean value')
    }

    // Account information validation for updates
    if (data.accountInformation !== undefined) {
      const { accountName, accountNumber, bankName, branchName } = data.accountInformation
      
      if (accountName || accountNumber || bankName || branchName) {
        if (!accountName || accountName.trim().length === 0) {
          errors.push('Account name is required when account information is provided')
        }
        if (!accountNumber || accountNumber.trim().length === 0) {
          errors.push('Account number is required when account information is provided')
        }
        if (!bankName || bankName.trim().length === 0) {
          errors.push('Bank name is required when account information is provided')
        }
        if (!branchName || branchName.trim().length === 0) {
          errors.push('Branch name is required when account information is provided')
        }
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(', ')}`)
    }

    return true
  }

  // 🎯 Utility Methods

  // Format supplier data for display
  formatSupplierForDisplay(supplier) {
    if (!supplier) return null

    return {
      id: supplier._id,
      name: supplier.name,
      mobile: supplier.mobile,
      contactPerson: supplier.contactPerson,
      email: supplier.email || 'N/A',
      address: supplier.address || 'N/A',
      accountInformation: supplier.accountInformation || {},
      totalPurchases: supplier.totalPurchases || 0,
      totalPaid: supplier.totalPaid || 0,
      balanceDue: supplier.balanceDue || 0,
      isActive: supplier.isActive !== false,
      createdAt: supplier.createdAt,
      updatedAt: supplier.updatedAt,
      createdBy: supplier.createdBy?.username || 'Unknown'
    }
  }

  // Format currency for display
  formatCurrency(amount) {
    if (typeof amount === 'number') {
      return `Rs. ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    return 'Rs. 0.00'
  }

  // Calculate balance due
  calculateBalanceDue(totalPurchases, totalPaid) {
    const purchases = parseFloat(totalPurchases) || 0
    const paid = parseFloat(totalPaid) || 0
    return purchases - paid
  }

  // Validate phone number format
  isValidPhoneNumber(phone) {
    if (!phone) return false
    const phoneRegex = /^[0-9+\-\s()]+$/
    return phoneRegex.test(phone) && phone.trim().length >= 8 && phone.trim().length <= 20
  }

  // Validate email format
  isValidEmail(email) {
    if (!email) return true // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Get supplier status badge color
  getStatusBadgeColor(isActive) {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
  }

  // Search suppliers locally (client-side filtering)
  searchSuppliersLocally(suppliers, searchTerm) {
    if (!searchTerm || !Array.isArray(suppliers)) return suppliers
    
    const lowercaseSearch = searchTerm.toLowerCase()
    return suppliers.filter(supplier => {
      const name = supplier.name?.toLowerCase() || ''
      const mobile = supplier.mobile?.toLowerCase() || ''
      const email = supplier.email?.toLowerCase() || ''
      const contactPerson = supplier.contactPerson?.toLowerCase() || ''
      
      return name.includes(lowercaseSearch) || 
             mobile.includes(lowercaseSearch) || 
             email.includes(lowercaseSearch) ||
             contactPerson.includes(lowercaseSearch)
    })
  }

  // Filter suppliers by status
  filterSuppliersByStatus(suppliers, status) {
    if (!Array.isArray(suppliers)) return []
    if (status === 'all') return suppliers
    
    const isActive = status === 'active'
    return suppliers.filter(supplier => supplier.isActive === isActive)
  }

  // Get financial data for a specific supplier from GRN data (includes unpaid GRNs)
  async getSupplierFinancialData(supplierId) {
    try {
      console.log('💰 Fetching supplier financial data from GRNs for supplier:', supplierId)
      
      // Fetch all GRNs using the service (uses VITE_BACKEND_URL from env)
      const grnsResponse = await GRNService.getAllGRNs({ limit: 1000 })
      const grns = grnsResponse?.data?.goodsReceiptNotes || grnsResponse?.data?.grns || grnsResponse?.data || []
      
      // Also fetch GRN payments for paid amounts
      const paymentsResponse = await GRNService.getAllGRNPayments({ limit: 1000 })
      const payments = paymentsResponse?.data?.payments || paymentsResponse?.data?.grnPayments || paymentsResponse?.data || []
      
      console.log(`💰 Fetched ${grns.length} GRNs and ${payments.length} payments for supplier: ${supplierId}`)
      
      // Calculate total purchases from ALL GRNs for this supplier (paid or unpaid)
      let totalPurchases = 0
      let grnCount = 0
      
      grns.forEach(grn => {
        const supplierFromPO = grn?.purchaseOrder?.supplier
        const supplierId_grn = typeof supplierFromPO === 'object' 
          ? supplierFromPO?._id 
          : supplierFromPO
        
        const match = supplierId_grn === supplierId ||
                      supplierId_grn?.toString() === supplierId?.toString()
        
        if (match) {
          totalPurchases += parseFloat(grn.totalAmount || 0)
          grnCount += 1
        }
      })
      
      // Calculate total paid from GRN payments
      let totalPaid = 0
      const supplierPayments = []
      
      payments.forEach(payment => {
        const supplierFromPayment = payment.grn?.purchaseOrder?.supplier
        const supplierId_payment = typeof supplierFromPayment === 'object'
          ? supplierFromPayment?._id
          : payment.supplier?._id || payment.supplierId
        
        const match = supplierId_payment === supplierId ||
                      supplierId_payment?.toString() === supplierId?.toString()
        
        if (match) {
          const paidAmount = parseFloat(payment.amountPaid || 0)
          totalPaid += paidAmount
          supplierPayments.push({
            paymentId: payment._id,
            amount: paidAmount,
            paymentDate: payment.paymentDate || payment.createdAt,
            grnNumber: payment.grn?.grnNumber || 'N/A',
            paymentMethod: payment.paymentType || payment.paymentMethod || 'cash',
          })
        }
      })
      
      const balanceDue = totalPurchases - totalPaid
      
      // Determine payment status
      let paymentStatus = 'No Transactions'
      if (totalPurchases > 0) {
        if (balanceDue <= 0) {
          paymentStatus = 'Fully Paid'
        } else if (totalPaid > 0) {
          paymentStatus = 'Partially Paid'
        } else {
          paymentStatus = 'Unpaid'
        }
      }
      
      const financialData = {
        totalPurchases,
        totalPaid,
        balanceDue,
        paymentStatus,
        grnCount,
        paymentDetails: supplierPayments
      }
      
      console.log('✅ Supplier financial data calculated:', financialData)
      return { success: true, data: financialData }
      
    } catch (error) {
      console.error('❌ Error fetching supplier financial data:', error)
      return {
        success: false,
        data: {
          totalPurchases: 0,
          totalPaid: 0,
          balanceDue: 0,
          paymentStatus: 'No Data',
          grnCount: 0,
          paymentDetails: []
        },
        error: error.message
      }
    }
  }

  // Calculate supplier purchase totals from GRN data (includes ALL GRNs: paid & unpaid)
  async calculateSupplierPurchaseTotals(suppliers) {
    try {
      console.log('🏢 Calculating supplier purchase totals from GRN data (paid + unpaid)...')
      console.log(`📋 Processing ${suppliers?.length || 0} suppliers`)
      
      if (!Array.isArray(suppliers) || suppliers.length === 0) {
        console.log('⚠️ No suppliers to process')
        return suppliers
      }

      // Fetch ALL GRNs (not just payments) so unpaid GRNs are included in total
      console.log('📦 Fetching all GRNs for total purchase calculation...')
      const grnsResponse = await GRNService.getAllGRNs({ limit: 1000 })
      const grns = grnsResponse?.data?.goodsReceiptNotes || grnsResponse?.data?.grns || grnsResponse?.data || []
      
      // Also fetch GRN payments for the paid amounts
      console.log('💰 Fetching GRN payments for paid amount calculation...')
      const paymentsResponse = await GRNService.getAllGRNPayments({ limit: 1000 })
      const payments = paymentsResponse?.data?.payments || paymentsResponse?.data?.grnPayments || paymentsResponse?.data || []
      
      console.log(`📦 Found ${grns.length} GRNs and ${payments.length} payments`)
      
      if (!Array.isArray(grns) || grns.length === 0) {
        console.warn('⚠️ No GRN data available for purchase calculations')
        return suppliers.map(supplier => ({
          ...supplier,
          totalPurchases: 0,
          totalPaid: 0,
          grnCount: 0
        }))
      }

      // Log supplier IDs for matching
      console.log('🏢 Supplier IDs to match:', suppliers.map(s => ({ name: s.name, id: s._id })))

      // Build a map of GRN -> supplier ID for quick lookup
      // GRN references supplier via purchaseOrder.supplier
      const grnSupplierMap = {}
      grns.forEach(grn => {
        const supplierFromPO = grn?.purchaseOrder?.supplier
        const supplierId = typeof supplierFromPO === 'object'
          ? supplierFromPO?._id?.toString()
          : supplierFromPO?.toString()
        if (supplierId && grn._id) {
          grnSupplierMap[grn._id.toString()] = {
            supplierId,
            totalAmount: parseFloat(grn.totalAmount || 0),
            grnNumber: grn.grnNumber
          }
        }
      })

      // Build a map of supplier ID -> total paid from GRN payments
      const supplierPaidMap = {}
      payments.forEach(payment => {
        const supplierFromPayment = payment.grn?.purchaseOrder?.supplier
        const supplierId = typeof supplierFromPayment === 'object'
          ? supplierFromPayment?._id?.toString()
          : (payment.supplier?._id?.toString() || payment.supplierId?.toString())
        
        if (supplierId) {
          if (!supplierPaidMap[supplierId]) {
            supplierPaidMap[supplierId] = 0
          }
          supplierPaidMap[supplierId] += parseFloat(payment.amountPaid || 0)
        }
      })

      // Calculate totals for each supplier
      const suppliersWithTotals = suppliers.map(supplier => {
        const supplierIdStr = supplier._id?.toString()
        
        // Sum all GRN totals for this supplier (paid or unpaid)
        let totalPurchases = 0
        let grnCount = 0
        
        Object.values(grnSupplierMap).forEach(grnInfo => {
          if (grnInfo.supplierId === supplierIdStr) {
            totalPurchases += grnInfo.totalAmount
            grnCount += 1
          }
        })
        
        // Get total paid for this supplier from payments
        const totalPaid = supplierPaidMap[supplierIdStr] || 0
        
        console.log(`💰 Supplier "${supplier.name}": GRNs=${grnCount}, Total Purchases=Rs. ${totalPurchases.toFixed(2)}, Total Paid=Rs. ${totalPaid.toFixed(2)}`)

        return {
          ...supplier,
          totalPurchases,
          totalPaid,
          grnCount
        }
      })

      const grandTotalPurchases = suppliersWithTotals.reduce((sum, s) => sum + (s.totalPurchases || 0), 0)
      const grandTotalPaid = suppliersWithTotals.reduce((sum, s) => sum + (s.totalPaid || 0), 0)
      console.log(`✅ Supplier totals calculated - Total Purchases: Rs. ${grandTotalPurchases.toFixed(2)}, Total Paid: Rs. ${grandTotalPaid.toFixed(2)}`)
      return suppliersWithTotals

    } catch (error) {
      console.error('❌ Error calculating supplier purchase totals:', error)
      console.error('❌ Error details:', error.message)
      return suppliers.map(supplier => ({
        ...supplier,
        totalPurchases: supplier.totalPurchases || 0,
        totalPaid: supplier.totalPaid || 0,
        grnCount: 0
      }))
    }
  }
}

export default new SuppliersService()